import { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Loader2, Sparkles, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { apiGet, apiDelete } from '../lib/api';
import { StatusPill, FormatBadge, isInFlight } from '../components/skill-studio/StatusPill';
import { NewDeliverableModal } from '../components/skill-studio/NewDeliverableModal';
import { DeliverableDetail } from '../components/skill-studio/DeliverableDetail';

const DeckCard = ({ d, onOpen, onDelete }) => (
  <Card className="cursor-pointer hover:border-brand/40 transition-colors" onClick={() => onOpen(d.id)} data-testid={`deck-card-${d.id}`}>
    <CardHeader className="pb-3">
      <div className="flex items-start justify-between gap-2">
        <CardTitle className="text-base text-text-primary line-clamp-2">{d.title}</CardTitle>
        <button onClick={(e) => { e.stopPropagation(); onDelete(d); }} className="text-text-muted hover:text-red-600 transition-colors flex-shrink-0"
          data-testid={`deck-delete-${d.id}`} aria-label="Delete deliverable">
          <Trash2 size={14} />
        </button>
      </div>
    </CardHeader>
    <CardContent className="space-y-2 text-sm">
      <div className="flex items-center gap-2 text-text-secondary">
        <span className="font-medium">{d.client_label || d.client_name}</span><span className="text-text-muted">·</span><span>{d.period}</span>
      </div>
      <div className="flex items-center justify-between"><StatusPill status={d.status} /><FormatBadge format={d.output_format} /></div>
    </CardContent>
  </Card>
);

const useDecks = (selectedId) => {
  const [decks, setDecks] = useState([]);
  const [meta, setMeta] = useState({ deck_types: [], engine_ready: true });
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadDecks = useCallback(async () => {
    const res = await apiGet('/api/presentations');
    if (res.ok) setDecks((await res.json()).presentations || []);
  }, []);
  const loadSelected = useCallback(async () => {
    if (!selectedId) { setSelected(null); return; }
    const res = await apiGet(`/api/presentations/${selectedId}`);
    if (res.ok) setSelected((await res.json()).deck);
  }, [selectedId]);

  useEffect(() => {
    Promise.all([loadDecks(), apiGet('/api/presentations/types').then((r) => r.json()).then(setMeta)])
      .catch(() => {}).finally(() => setLoading(false));
  }, [loadDecks]);
  useEffect(() => { loadSelected(); }, [loadSelected]);
  useEffect(() => {
    if (!decks.some(isInFlight) && !isInFlight(selected)) return undefined;
    const id = setInterval(() => { loadDecks(); loadSelected(); }, 5000);
    return () => clearInterval(id);
  }, [decks, selected, loadDecks, loadSelected]);

  return { decks, meta, selected, loading, loadDecks, loadSelected };
};

export default function DecksView() {
  const [selectedId, setSelectedId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const { decks, meta, selected, loading, loadDecks, loadSelected } = useDecks(selectedId);

  const handleDelete = async (deck) => {
    if (!window.confirm(`Delete "${deck.title}"?`)) return;
    const res = await apiDelete(`/api/presentations/${deck.id}`);
    if (!res.ok) { toast.error('Delete failed'); return; }
    toast.success('Deleted');
    if (selectedId === deck.id) setSelectedId(null);
    loadDecks();
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-brand" size={28} /></div>;

  if (selectedId && selected) {
    return <DeliverableDetail deck={selected} onBack={() => setSelectedId(null)} onRefresh={() => { loadSelected(); loadDecks(); }} />;
  }

  return (
    <div className="space-y-6" data-testid="decks-view">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-text-primary">Skill Studio</h1>
          <p className="text-text-secondary mt-1">BCM-branded PowerPoint decks and Word documents, built by Claude with the BCM brand skill.</p>
        </div>
        <Button onClick={() => setShowNew(true)} disabled={!meta.engine_ready} data-testid="new-deck-button">
          <Plus size={16} className="mr-1.5" /> New Deliverable
        </Button>
      </div>
      {!meta.engine_ready && (
        <Card className="border-amber-300 bg-amber-50" data-testid="engine-not-ready"><CardContent className="pt-6 flex gap-3 text-sm text-amber-900">
          <AlertTriangle size={18} className="text-amber-700" /> The Anthropic API key isn't configured yet, so generation is paused.
        </CardContent></Card>
      )}
      {decks.length === 0 ? (
        <Card><CardContent className="py-16 text-center" data-testid="decks-empty">
          <Sparkles className="mx-auto text-text-muted mb-3" size={36} />
          <p className="text-text-primary font-semibold">Nothing generated yet</p>
          <p className="text-sm text-text-muted mt-1">Click "New Deliverable" to build your first BCM deck or document.</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {decks.map((d) => <DeckCard key={d.id} d={d} onOpen={setSelectedId} onDelete={handleDelete} />)}
        </div>
      )}
      {showNew && (
        <NewDeliverableModal deckTypes={meta.deck_types} onClose={() => setShowNew(false)}
          onCreated={(deck) => { setShowNew(false); loadDecks(); setSelectedId(deck.id); }} />
      )}
    </div>
  );
}
